import React, { useMemo, useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

const months = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const defaultState = {
  vendorId: "",
  effectiveMonth: "",
  effectiveYear: new Date().getFullYear(),
  kwhTier: "",
  generationRatePerKwh: "",
  distributionRatePerKwh: "",
  transmissionRatePerKwh: "",
  discountRatePerKwh: "",
};

const normalizeInitial = (initial) => {
  if (!initial) return defaultState;
  return {
    vendorId: initial.vendorId ?? "",
    effectiveMonth: initial.effectiveMonth ?? "",
    effectiveYear: initial.effectiveYear ?? new Date().getFullYear(),
    kwhTier: initial.kwhTier ?? "",
    generationRatePerKwh: initial.generationRatePerKwh ?? "",
    distributionRatePerKwh: initial.distributionRatePerKwh ?? "",
    transmissionRatePerKwh: initial.transmissionRatePerKwh ?? "",
    discountRatePerKwh: initial.discountRatePerKwh ?? "",
  };
};

const RateReferenceForm = ({ initialValue, vendors, onSubmit, onClose }) => {
  const [form, setForm] = useState(normalizeInitial(initialValue));
  const [saving, setSaving] = useState(false);

  const vendorsOptions = useMemo(() => {
    return (vendors || []).filter((v) => v?.id && v?.name);
  }, [vendors]);

  const onFieldChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const isMissingRequired =
    !form.vendorId ||
    !form.effectiveMonth ||
    !form.effectiveYear ||
    form.generationRatePerKwh === "" ||
    form.distributionRatePerKwh === "" ||
    form.transmissionRatePerKwh === "" ||
    form.discountRatePerKwh === "";

  const handleSubmit = async () => {
    if (isMissingRequired || saving) return;

    const payload = {
      vendorId: Number(form.vendorId),
      effectiveMonth: Number(form.effectiveMonth),
      effectiveYear: Number(form.effectiveYear),
      generationRatePerKwh: Number(form.generationRatePerKwh),
      distributionRatePerKwh: Number(form.distributionRatePerKwh),
      transmissionRatePerKwh: Number(form.transmissionRatePerKwh),
      discountRatePerKwh: Number(form.discountRatePerKwh),
    };

    if (form.kwhTier !== "" && form.kwhTier !== null && form.kwhTier !== undefined) {
      payload.kwhTier = Number(form.kwhTier);
    }

    setSaving(true);
    const success = await onSubmit(payload);
    setSaving(false);
    if (success) onClose();
  };

  return (
    <>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="rate-reference-vendor-label">Vendor</InputLabel>
              <Select
                labelId="rate-reference-vendor-label"
                name="vendorId"
                value={form.vendorId}
                label="Vendor"
                onChange={onFieldChange}
              >
                {vendorsOptions.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    {vendor.vendorNum ? `${vendor.vendorNum} - ` : ""}
                    {vendor.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="rate-reference-month-label">Month</InputLabel>
              <Select
                labelId="rate-reference-month-label"
                name="effectiveMonth"
                value={form.effectiveMonth}
                label="Month"
                onChange={onFieldChange}
              >
                {months.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Year"
              name="effectiveYear"
              type="number"
              value={form.effectiveYear}
              onChange={onFieldChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="kWh Tier (Optional)"
              name="kwhTier"
              type="number"
              value={form.kwhTier}
              onChange={onFieldChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Generation Rate per kWh"
              name="generationRatePerKwh"
              type="number"
              value={form.generationRatePerKwh}
              onChange={onFieldChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Distribution Rate per kWh"
              name="distributionRatePerKwh"
              type="number"
              value={form.distributionRatePerKwh}
              onChange={onFieldChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Transmission Rate per kWh"
              name="transmissionRatePerKwh"
              type="number"
              value={form.transmissionRatePerKwh}
              onChange={onFieldChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Discount Rate per kWh"
              name="discountRatePerKwh"
              type="number"
              value={form.discountRatePerKwh}
              onChange={onFieldChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={isMissingRequired || saving}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </DialogActions>
    </>
  );
};

export default RateReferenceForm;
